"use client";

import { ChangeEvent, FormEvent, useMemo, useState, useEffect } from "react";
import { useLocale, useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { PageTransition } from "@/components/Transition";
import { ResponsiveContainer } from "@/components/ResponsiveContainer";
import { useToast } from "@/hooks/use-toast";
import { Heading } from "@/components/ui/typography";
import { Button } from "@/components/ui/button";

const CONTACT_EMAIL = "support@tukera.com";
export default function ContactPage() {
  const { toast } = useToast();
  const t = useTranslations("contact");
  const locale = useLocale();
  const searchParams = useSearchParams();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Pre-fill subject from URL parameter
  useEffect(() => {
    const subjectParam = searchParams?.get("subject");
    if (subjectParam) {
      setFormData((prev) => ({
        ...prev,
        subject: subjectParam,
      }));
    }
  }, [searchParams]);

  const isDev = process.env.NODE_ENV !== "production";
  const h1w = searchParams?.get("h1w");
  const h1WeightClass = useMemo(() => {
    if (!isDev) return undefined;
    switch ((h1w || "").toLowerCase()) {
      case "bold":
        return "font-bold";
      case "semibold":
        return "font-semibold";
      case "medium":
        return "font-medium";
      default:
        return undefined;
    }
  }, [h1w, isDev]);

  const handleChange = (
    event: ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
    // Hide success message when user starts typing
    if (submitSuccess) {
      setSubmitSuccess(false);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (formData.name.length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }
    if (formData.name.length > 100) {
      newErrors.name = "Name must be less than 100 characters";
    }
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.subject) {
      newErrors.subject = "Please select a subject";
    }
    if (formData.message.length < 10) {
      newErrors.message = "Message must be at least 10 characters";
    }
    if (formData.message.length > 2000) {
      newErrors.message = "Message must be less than 2000 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch("/api/contact/messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          locale,
        }),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        throw new Error(
          errorBody?.message || "Failed to submit contact message",
        );
      }

      toast({
        title: t("toast.successTitle"),
        description: t("toast.successDescription"),
        duration: 5000,
      });

      setSubmitSuccess(true);
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      });
      setErrors({});
    } catch (error) {
      console.error("Failed to submit contact message", error);
      toast({
        title: t("toast.errorTitle"),
        description: t("toast.errorDescription"),
        duration: 5000,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const subjectOptions = [
    { value: "general", label: t("subjectOptions.general") },
    { value: "support", label: t("subjectOptions.support") },
    { value: "feedback", label: t("subjectOptions.feedback") },
    { value: "partnership", label: t("subjectOptions.partnership") },
    { value: "other", label: t("subjectOptions.other") },
  ];

  return (
    <PageTransition>
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-rose-50 transition-colors dark:bg-gradient-to-b dark:from-background dark:via-background/95 dark:to-muted/20">
        <ResponsiveContainer maxWidth="full" className="px-4 md:px-6 lg:px-10">
          <div className="mx-auto w-full max-w-7xl py-12 md:py-16">
            <div className="rounded-3xl border border-white/60 bg-white/85 px-6 py-8 shadow-xl backdrop-blur-sm transition-colors dark:border-border/60 dark:bg-card/65 dark:shadow-2xl md:px-14 md:py-12">
              <header className="space-y-3 text-center md:text-left">
                <Heading
                  variant="h1"
                  as="h1"
                  className={`text-gray-900 dark:text-white font-semibold ${h1WeightClass ?? ""}`}
                >
                  {t("title")}
                </Heading>
                <p className="text-base leading-relaxed text-slate-600 dark:text-slate-300">
                  {t("description")}
                </p>
              </header>

              <div className="mt-10 grid gap-12 lg:mt-12 lg:grid-cols-[1.2fr,1fr]">
                <section className="space-y-6">
                  <div className="rounded-2xl border border-white/70 bg-sky-50/80 p-6 shadow-sm backdrop-blur-sm transition-colors dark:border-border/50 dark:bg-card/60 dark:shadow-lg">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {t("contactInfoTitle")}
                    </h2>
                    <div className="mt-6 space-y-6">
                      <div className="flex items-start gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/80 text-sky-600 shadow-sm dark:bg-background/80 dark:text-sky-300">
                          <svg
                            className="h-6 w-6"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M3 8L10.89 13.26C11.57 13.71 12.43 13.71 13.11 13.26L21 8"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <rect
                              x="3"
                              y="5"
                              width="18"
                              height="14"
                              rx="2"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                            {t("emailLabel")}
                          </h3>
                          <p className="mt-1 text-sm text-muted-foreground md:text-base">
                            {CONTACT_EMAIL}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-6 md:grid-cols-2">
                    <div className="rounded-2xl border border-white/70 bg-emerald-50/80 p-6 shadow-sm backdrop-blur-sm transition-colors dark:border-border/50 dark:bg-card/60 dark:shadow-lg">
                      <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {t("workHoursTitle")}
                      </h2>
                      <div className="mt-4 space-y-2 text-base text-slate-700 dark:text-slate-300">
                        <p>{t("workdayHours")}</p>
                        <p>{t("weekendHours")}</p>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-white/70 bg-violet-50/80 p-6 shadow-sm backdrop-blur-sm transition-colors dark:border-border/50 dark:bg-card/60 dark:shadow-lg">
                      <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {t("socialTitle")}
                      </h2>
                      <div className="mt-4 flex flex-wrap gap-3">
                        <a
                          href="https://x.com/tukera_ai"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 rounded-xl bg-white/80 px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:text-sky-600 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-sky-100 dark:bg-background/80 dark:text-slate-200 dark:hover:text-sky-300 dark:focus-visible:ring-sky-500/40"
                          aria-label={t("socialTwitter")}
                        >
                          <svg
                            className="h-5 w-5"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                          </svg>
                          {t("socialTwitter")}
                        </a>
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 rounded-xl bg-white/80 px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:text-sky-600 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-sky-100 dark:bg-background/80 dark:text-slate-200 dark:hover:text-sky-300 dark:focus-visible:ring-sky-500/40"
                          aria-label={t("socialInstagram")}
                        >
                          <svg
                            className="h-5 w-5"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
                            />
                          </svg>
                          {t("socialInstagram")}
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 rounded-xl bg-white/80 px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:text-sky-600 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-sky-100 dark:bg-background/80 dark:text-slate-200 dark:hover:text-sky-300 dark:focus-visible:ring-sky-500/40"
                          aria-label={t("socialFacebook")}
                        >
                          <svg
                            className="h-5 w-5"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M22 12C22 6.477 17.523 2 12 2C6.477 2 2 6.477 2 12C2 16.991 5.657 21.128 10.438 21.878V14.89H7.898V12H10.438V9.797C10.438 7.29 11.93 5.906 14.215 5.906C15.309 5.906 16.453 6.101 16.453 6.101V8.561H15.193C13.95 8.561 13.563 9.332 13.563 10.123V12H16.33L15.887 14.89H13.563V21.878C18.343 21.128 22 16.991 22 12Z"
                            />
                          </svg>
                          {t("socialFacebook")}
                        </button>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="rounded-2xl border border-white/70 bg-white/90 p-6 shadow-sm backdrop-blur-sm transition-colors dark:border-border/50 dark:bg-card/70 dark:shadow-lg md:p-10">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {t("formTitle")}
                  </h2>

                  {submitSuccess && (
                    <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-800 dark:bg-emerald-900/20">
                      <div className="flex items-start gap-3">
                        <svg
                          className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0 dark:text-emerald-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-emerald-900 dark:text-emerald-100">
                            {t("toast.successTitle")}
                          </p>
                          <p className="mt-1 text-sm text-emerald-700 dark:text-emerald-300">
                            {t("toast.successDescription")}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="mt-6 space-y-5">
                    <div className="space-y-2">
                      <label
                        htmlFor="name"
                        className="text-base font-semibold text-gray-900 dark:text-white"
                      >
                        {t("form.name")}
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className={`w-full rounded-xl border px-4 py-3 text-base text-slate-900 shadow-sm transition focus:outline-none focus:ring-4 dark:text-white ${errors.name ? "border-red-300 bg-red-50 focus:border-red-400 focus:ring-red-100 dark:border-red-700 dark:bg-red-900/20 dark:focus:border-red-500 dark:focus:ring-red-500/40" : "border-slate-200 bg-white/80 focus:border-sky-400 focus:ring-sky-100 dark:border-border/60 dark:bg-background/80 dark:focus:border-sky-500 dark:focus:ring-sky-500/40"}`}
                      />
                      {errors.name && (
                        <p className="text-sm text-red-600 dark:text-red-400">
                          {errors.name}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label
                        htmlFor="email"
                        className="text-base font-semibold text-gray-900 dark:text-white"
                      >
                        {t("form.email")}
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className={`w-full rounded-xl border px-4 py-3 text-base text-slate-900 shadow-sm transition focus:outline-none focus:ring-4 dark:text-white ${errors.email ? "border-red-300 bg-red-50 focus:border-red-400 focus:ring-red-100 dark:border-red-700 dark:bg-red-900/20 dark:focus:border-red-500 dark:focus:ring-red-500/40" : "border-slate-200 bg-white/80 focus:border-sky-400 focus:ring-sky-100 dark:border-border/60 dark:bg-background/80 dark:focus:border-sky-500 dark:focus:ring-sky-500/40"}`}
                      />
                      {errors.email && (
                        <p className="text-sm text-red-600 dark:text-red-400">
                          {errors.email}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label
                        htmlFor="subject"
                        className="text-base font-semibold text-gray-900 dark:text-white"
                      >
                        {t("form.subject")}
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className={`w-full rounded-xl border px-4 py-3 text-base text-slate-900 shadow-sm transition focus:outline-none focus:ring-4 dark:text-white ${errors.subject ? "border-red-300 bg-red-50 focus:border-red-400 focus:ring-red-100 dark:border-red-700 dark:bg-red-900/20 dark:focus:border-red-500 dark:focus:ring-red-500/40" : "border-slate-200 bg-white/80 focus:border-sky-400 focus:ring-sky-100 dark:border-border/60 dark:bg-background/80 dark:focus:border-sky-500 dark:focus:ring-sky-500/40"}`}
                      >
                        <option value="">{t("form.subjectPlaceholder")}</option>
                        {subjectOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {errors.subject && (
                        <p className="text-sm text-red-600 dark:text-red-400">
                          {errors.subject}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label
                        htmlFor="message"
                        className="text-base font-semibold text-gray-900 dark:text-white"
                      >
                        {t("form.message")}
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        className={`w-full rounded-xl border px-4 py-3 text-base text-slate-900 shadow-sm transition focus:outline-none focus:ring-4 dark:text-white ${errors.message ? "border-red-300 bg-red-50 focus:border-red-400 focus:ring-red-100 dark:border-red-700 dark:bg-red-900/20 dark:focus:border-red-500 dark:focus:ring-red-500/40" : "border-slate-200 bg-white/80 focus:border-sky-400 focus:ring-sky-100 dark:border-border/60 dark:bg-background/80 dark:focus:border-sky-500 dark:focus:ring-sky-500/40"}`}
                      />
                      {errors.message && (
                        <p className="text-sm text-red-600 dark:text-red-400">
                          {errors.message}
                        </p>
                      )}
                    </div>

                    <Button
                      type="submit"
                      variant="soft"
                      disabled={isSubmitting}
                      className="w-full md:w-auto"
                    >
                      {isSubmitting ? t("form.submitting") : t("form.submit")}
                    </Button>
                  </form>
                </section>
              </div>
            </div>
          </div>
        </ResponsiveContainer>
      </div>
    </PageTransition>
  );
}
